import React, { useState } from 'react';
import {
  Box,
  Text,
  Button,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  TableToolbar,
  TableToolbarActions,
  RefreshIcon,
  TablePagination,
  ListView,
  ListViewFilters,
  QuickFilterGroup,
  QuickFilter,
  CloseIcon,
  CheckIcon,
  InfoIcon,
  Tooltip,
  TooltipInteractiveWrapper,
  Link,
  ChevronRightIcon,
} from '@razorpay/blade/components';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { usePagination } from 'hooks/usePagination';
import {
  formatPaginationRange,
  getVerificationStatusColor,
  getVerificationStatusTitle,
  isAccountUnverified,
} from './utils';
import { useFilters } from 'hooks/useFilters';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { useWorkflowDetailsView } from '../useWorkflowDetailsView';
import { DetailsView } from './DetailsView';
import { useQuery } from 'react-query';
import employeeBankAccountApis, {
  BankAccountRequest,
  GET_BANK_ACCOUNT_REQUESTS,
  GetBankAccountRequestsQueryParams,
} from 'api/employeeBankAccount';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyView } from './EmptyView';
import { BulkApproveModal } from './components/BulkApproveModal';
import { BulkRejectModal } from './components/BulkRejectModal';
import {
  bankAccountApprovalStatus,
  bankAccountVerificationStatus,
} from 'components/EmployeeDetails/BankAccountDetails/data';
import pluralize from 'pluralize';
const filtersMap = {
  status: 'status',
} as const;

export const PendingApproval = () => {
  const [selectedRequests, setSelectedRequests] = useState<Record<string, BankAccountRequest>>({});
  const [bulkApprovalAction, setBulkApprovalAction] = useState<'bulkApprove' | 'bulkReject'>();
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });
  const { openDetailsView } = useWorkflowDetailsView();
  const { currentFilters, applyFilter } = useFilters({ filtersMap });

  const queryParams: GetBankAccountRequestsQueryParams = {
    count: itemsPerPage,
    skip: currentPage * itemsPerPage,
    approval_statuses: [bankAccountApprovalStatus.pending],
    ...(!isRxCurrentAccountMerchant || !currentFilters.status || currentFilters.status === 'all'
      ? {}
      : {
          verification_statuses:
            currentFilters.status === 'unverified'
              ? [
                  bankAccountVerificationStatus.failed,
                  bankAccountVerificationStatus.invalid,
                  bankAccountVerificationStatus.created,
                ]
              : [currentFilters.status],
        }),
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: [GET_BANK_ACCOUNT_REQUESTS, queryParams],
    queryFn: () => {
      return employeeBankAccountApis.getBankAccountRequests(queryParams);
    },
  });

  const bankAccountRequests = data?.data?.items ?? [];

  const selectedRowsCount = Object.keys(selectedRequests).length;

  const onDismissBulkApprovalModal = () => {
    setBulkApprovalAction(undefined);
  };

  const onBulkApprovalSuccess = (failedIds: string[]) => {
    setSelectedRequests((prev) => {
      const prevIds = Object.keys(prev);
      if (failedIds.length) {
        const newSelectedRequests = { ...prev };
        prevIds.forEach((id) => {
          if (!failedIds.includes(id)) delete newSelectedRequests[id];
        });
        return newSelectedRequests;
      }
      return {};
    });
  };

  if (isError) {
    return (
      <Box marginTop="spacing.6">
        <ErrorView onRefetch={refetch} />
      </Box>
    );
  }

  return (
    <Box marginTop="spacing.6">
      <ListView>
        {isRxCurrentAccountMerchant && (
          <ListViewFilters
            quickFilters={
              <QuickFilterGroup
                onChange={({ values }) => applyFilter(filtersMap.status, values[0])}
                selectionType="single"
                value={currentFilters.status ?? 'all'}>
                <QuickFilter title="All Requests" value="all" />
                <QuickFilter title="Match" value={bankAccountVerificationStatus.verified} />
                <QuickFilter title="No Match" value={bankAccountVerificationStatus.mismatch} />
                <QuickFilter title="Unverified" value="unverified" />
              </QuickFilterGroup>
            }
            children={undefined}
          />
        )}

        <Table
          isLoading={isLoading}
          gridTemplateColumns={
            bankAccountRequests.length > 0
              ? isRxCurrentAccountMerchant
                ? '68px Repeat(4, 1fr) 100px'
                : 'Repeat(3, 1fr) 100px'
              : undefined
          }
          isRefreshing={isFetching}
          multiSelectTrigger="checkbox"
          selectionType={isRxCurrentAccountMerchant ? 'multiple' : 'none'}
          onSelectionChange={(props) => {
            const data = props.selectedIds.reduce((acc, id) => {
              const item = bankAccountRequests.find((item) => item.id === id);

              acc[id] = item ?? selectedRequests[id];
              return acc;
            }, {} as Record<string, BankAccountRequest>);
            setSelectedRequests(data);
          }}
          data={{
            nodes: bankAccountRequests.map((item) => ({
              id: item.id,
              data: {
                requestDate: item.created_at ? new Date(item.created_at) : null,
                employee: item.employee_name,
                beneficiary: item.beneficiary_name,
                status: item.verification_status,
              },
            })),
          }}
          pagination={
            bankAccountRequests.length > 0 ? (
              <TablePagination
                currentPage={currentPage}
                paginationType="server"
                showPageSizePicker={true}
                onPageChange={({ page }) => {
                  setCurrentPage(page);
                }}
                onPageSizeChange={({ pageSize }) => {
                  setItemsPerPage(pageSize);
                  setCurrentPage(0);
                }}
                totalItemCount={data?.data?.total_count ?? 0}
              />
            ) : undefined
          }
          toolbar={
            <TableToolbar
              selectedTitle={`${selectedRowsCount} ${pluralize(
                'Request',
                selectedRowsCount,
              )} Selected`}
              title={
                bankAccountRequests.length > 0
                  ? `Showing ${formatPaginationRange({
                      totalItems: data?.data?.count ?? 0,
                      currentPage,
                      itemsPerPage,
                    })} Pending Approvals`
                  : ''
              }>
              <TableToolbarActions>
                <Box display="flex" gap="spacing.5">
                  <Button
                    icon={RefreshIcon}
                    variant={'tertiary'}
                    accessibilityLabel="Refresh Pending Requests"
                    onClick={refetch}
                    isDisabled={isFetching || isLoading}
                  />
                  {selectedRowsCount > 0 && (
                    <Button
                      color="negative"
                      icon={CloseIcon}
                      variant="secondary"
                      isDisabled={isFetching || isLoading}
                      onClick={() => {
                        setBulkApprovalAction('bulkReject');
                      }}>
                      Reject
                    </Button>
                  )}
                  {selectedRowsCount > 0 && (
                    <Button
                      icon={CheckIcon}
                      isDisabled={isFetching || isLoading}
                      onClick={() => {
                        setBulkApprovalAction('bulkApprove');
                      }}>
                      Approve
                    </Button>
                  )}
                </Box>
              </TableToolbarActions>
            </TableToolbar>
          }>
          {(nodes) =>
            bankAccountRequests.length > 0 ? (
              <>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Request Date
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Employee Name
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip
                        content={
                          isRxCurrentAccountMerchant
                            ? 'The account holder name is obtained either through penny testing or is provided directly by the employee.'
                            : 'The account holder name is provided directly by the employee.'
                        }>
                        <TooltipInteractiveWrapper>
                          <Box display="flex" gap="spacing.2" alignItems="center">
                            <Text size="medium" weight="semibold">
                              Account Holder Name
                            </Text>
                            <InfoIcon />
                          </Box>
                        </TooltipInteractiveWrapper>
                      </Tooltip>
                    </TableHeaderCell>
                    {isRxCurrentAccountMerchant && (
                      <TableHeaderCell>
                        <Tooltip content="Indicates whether the employee’s name matches the account holder’s name.">
                          <TooltipInteractiveWrapper>
                            <Box display="flex" gap="spacing.2" alignItems="center">
                              <Text size="medium" weight="semibold">
                                Name Match
                              </Text>
                              <InfoIcon />
                            </Box>
                          </TooltipInteractiveWrapper>
                        </Tooltip>
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell>{''}</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {nodes.map((item) => (
                    <TableRow
                      onClick={() => {
                        openDetailsView(Number(item.id));
                      }}
                      key={item.id}
                      item={item}>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {getFormattedDateValue({
                            date: item.data.requestDate,
                            formatString: dateFormats.dateShortMonthCommaYear,
                          })}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.employee}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.beneficiary ?? '--'}
                        </Text>
                      </TableCell>
                      {isRxCurrentAccountMerchant && (
                        <TableCell>
                          {isAccountUnverified(item.data.status) ? (
                            <Tooltip content="We were unable to verify the bank account details with the bank. Manual verification will be required.">
                              <TooltipInteractiveWrapper>
                                <Badge
                                  icon={InfoIcon}
                                  color={getVerificationStatusColor(item.data.status)}>
                                  {getVerificationStatusTitle(item.data.status)}
                                </Badge>
                              </TooltipInteractiveWrapper>
                            </Tooltip>
                          ) : (
                            <Badge color={getVerificationStatusColor(item.data.status)}>
                              {getVerificationStatusTitle(item.data.status)}
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell textAlign="right">
                        <Link
                          variant="button"
                          size="medium"
                          icon={ChevronRightIcon}
                          iconPosition="right"
                          onClick={() => openDetailsView(Number(item.id))}>
                          Review
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </>
            ) : (
              <EmptyView />
            )
          }
        </Table>
      </ListView>
      <BulkApproveModal
        isOpen={bulkApprovalAction === 'bulkApprove'}
        onDismiss={onDismissBulkApprovalModal}
        selectedRequests={selectedRequests}
        onSuccess={onBulkApprovalSuccess}
      />

      <BulkRejectModal
        isOpen={bulkApprovalAction === 'bulkReject'}
        onDismiss={onDismissBulkApprovalModal}
        selectedRequests={selectedRequests}
        onSuccess={onBulkApprovalSuccess}
      />
      <DetailsView />
    </Box>
  );
};
